import { createFileRoute } from "@tanstack/react-router";

import { PageChrome } from "../components/PageChrome";
import { useTrack } from "../lib/analytics";
import { canonical, documentHeaders } from "../lib/seo";

/**
 * Deep link into the installed app's deletion screen.
 *
 * ONE scheme for every environment — dev, preview and production all register
 * `fazly://` (apps/mobile/app.config.ts: `getDynamicAppConfig` returns the
 * same IDENTITY for all three, so that exactly one App ID, one Firebase app and
 * one RevenueCat app have to exist downstream).
 * So this link works against whichever build is installed, and there is no
 * `-dev` variant to keep in step.
 *
 * The app answers it in `lib/deleteAccountLink.ts` and lands on a ROOT-level
 * screen, which is what lets a subscriber whose plan expired reach deletion
 * without the paywall coming down.
 */
const APP_DEEP_LINK = "fazly://delete-account";

export const Route = createFileRoute("/delete")({
  headers: documentHeaders,
  head: () => ({
    meta: [{ title: "Delete your account — Fazly" }],
    links: [{ rel: "canonical", href: canonical("/delete") }],
  }),
  component: DeleteAccount,
});

function DeleteAccount() {
  const track = useTrack();

  return (
    <PageChrome title="Delete your account">
      <p className="mt-4 max-w-2xl text-[17px] leading-relaxed text-ink2">
        You can permanently delete your Fazly account and all of its data at any
        time, right from the app. If Fazly is installed, the button below takes
        you straight there.
      </p>

      <div className="mt-8 flex flex-col items-start gap-3">
        <a
          href={APP_DEEP_LINK}
          // The scheme only resolves if the app is installed, so a click here is
          // a signal in its own right: it separates people who already have
          // Fazly and want out from people who found this page by search.
          onClick={() => track("web_app_open_click", { from: "delete" })}
          className="rim inline-flex h-[54px] items-center justify-center bg-brand px-7 font-display text-[17px] font-bold text-onbrand"
        >
          open fazly
        </a>
        <span className="text-[13px] text-ink2">
          Opens the app to Profile → Delete account.
        </span>
      </div>

      {/* The list is the Privacy Policy's §9 said in the place somebody is
          actually standing when it matters. It was two placeholder lines ("your
          device profile and session", "all data associated with your device"),
          which is the kind of sentence that sounds complete and answers nothing:
          the person on this page wants to know whether their photographs go, and
          whether anything is kept. Both halves are now stated, and the second
          one — billing records survive — belongs here precisely because it is
          the part a deletion page is tempted to leave out. */}
      <section className="mt-12">
        <h2 className="font-display text-[22px] font-bold text-ink">
          What gets deleted
        </h2>
        <ul className="mt-3 max-w-2xl list-disc space-y-1.5 pl-5 text-[16px] leading-relaxed text-ink2">
          <li>Every scan, and every photograph behind it</li>
          <li>Your scores, your breakdown and your roadmap</li>
          <li>Your profile, your username and your setup answers</li>
          <li>Your routine history, streak, XP and achievements</li>
          <li>Your leaderboard entry and your notification settings</li>
        </ul>
        <p className="mt-4 max-w-2xl text-[16px] leading-relaxed text-ink2">
          One thing is kept: the record of any subscription you bought, because
          it is the proof of what was sold. It holds no photograph and no score.
          The full list is in the{" "}
          <a className="underline underline-offset-4" href="/privacy#retention">
            Privacy Policy
          </a>
          .
        </p>
      </section>

      <div className="brutal mt-8 max-w-2xl px-5 py-4 text-[15px] leading-relaxed text-ink2">
        Deletion is immediate and permanent — it can't be undone.
      </div>

      <section className="mt-12">
        <h2 className="font-display text-[22px] font-bold text-ink">
          Prefer to do it manually?
        </h2>
        <ol className="mt-3 max-w-2xl list-decimal space-y-1.5 pl-5 text-[16px] leading-relaxed text-ink2">
          <li>Open the Fazly app.</li>
          <li>Go to the Profile tab.</li>
          <li>Tap “Delete account” and confirm.</li>
        </ol>
      </section>
    </PageChrome>
  );
}
